import * as SentryVue from '@sentry/vue';
import type {ErrorEvent, EventHint} from '@sentry/core';
import {setTag} from '@sentry/core';
import {environment} from '@/config/environment';
import type {App, Plugin} from 'vue';
import {useRouter} from 'vue-router';
import {useToast} from '@/shared/components/toast/useToast';

export const sentryPlugin = {
    install(app) {
        addErrorHandling(app);
        app.runWithContext(() => {
            const router = useRouter();
            const options = getInitOptions(app, router);

            if (__IS_HYBRID_APP__) {
                import('@sentry/capacitor').then(async SentryCapacitor => {
                    //@ts-ignore
                    SentryCapacitor.init(options, SentryVue.init);

                    const {CapacitorUpdater} = await import('@capgo/capacitor-updater');
                    const {bundle} = await CapacitorUpdater.current();

                    // Set tag for native errors
                    SentryCapacitor.setTag('capgo.bundle_id', bundle.version);

                    // Set tag for JS errors
                    setTag('capgo.bundle_id', bundle.version);
                });
            } else {
                SentryVue.init(options);
            }
        });

        window.addEventListener('vite:preloadError', () => {
            if (!sessionStorage.getItem('preload-retry')) {
                sessionStorage.setItem('preload-retry', '1');
                window.location.reload();
            }
        });
    },
} satisfies Plugin;

type SentryVueInitOptions = NonNullable<Parameters<typeof SentryVue.init>[0]>;

const ignoredErrorMessages = [
    // Benign browser noise: the observer callback couldn't finish within one
    // frame; layout still settles on the next one.
    /ResizeObserver loop limit exceeded/i,
    /ResizeObserver loop completed with undelivered notifications/i,
    // Rejections without an Error object carry no stacktrace and nothing actionable.
    /Non-Error promise rejection captured/i,
    /Object captured as promise rejection/i,
    // Network noise (offline users, aborted navigations).
    /failed to fetch/i,
    /load failed/i,
    /networkerror when attempting to fetch resource/i,
    // Failed HTTP responses are tracked by the backend; the status-based drop in
    // beforeSend doesn't catch these because they carry no originalException.
    /HTTP Client Error with status code/i,
    // Chunk-load failures of stale clients after a deploy. vite:preloadError
    // (see below) already reloads the page once to recover.
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
    /Failed to fetch dynamically imported module/i,
    /Unable to preload CSS/i,
    // Leaflet teardown races while a map unmounts.
    /\(reading '_leaflet_pos'\)/i,
    /\(evaluating 'h\._leaflet_pos'\)/i,
];

// Errors originating from scripts we don't ship (browser extensions).
const deniedUrls = [
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari(-web)?-extension:\/\//i,
    /extensions\//i,
];

const stackTraceSentryExceptions = [/_getMapPanePos/];

const throttleLimits = {
    windowMs: 60_000,
    maxPerKey: 5,
    maxTotal: 30,
};

const throttleBuckets = new Map<string, number[]>();
let throttleTotal: number[] = [];

function getThrottleKey(event: ErrorEvent): string {
    if (event.fingerprint?.length) {
        return event.fingerprint.join('|');
    }

    const exception = event.exception?.values?.at(-1);
    const frame = exception?.stacktrace?.frames?.at(-1);

    return [exception?.type, exception?.value, frame?.filename, frame?.function].join('|');
}

// Client-side throttle so an error loop can't drain the Sentry quota before
// server-side protections kick in: identical events are capped per minute,
// with a global cap as safety net for loops producing varying messages.
function isThrottled(event: ErrorEvent): boolean {
    const now = Date.now();
    const windowStart = now - throttleLimits.windowMs;

    for (const [key, timestamps] of throttleBuckets) {
        const currentTimestamps = timestamps.filter(timestamp => timestamp > windowStart);
        if (currentTimestamps.length) {
            throttleBuckets.set(key, currentTimestamps);
        } else {
            throttleBuckets.delete(key);
        }
    }
    throttleTotal = throttleTotal.filter(timestamp => timestamp > windowStart);

    if (throttleTotal.length >= throttleLimits.maxTotal) {
        return true;
    }

    const key = getThrottleKey(event);
    const bucket = throttleBuckets.get(key) ?? [];

    if (bucket.length >= throttleLimits.maxPerKey) {
        return true;
    }

    bucket.push(now);
    throttleBuckets.set(key, bucket);
    throttleTotal.push(now);

    return false;
}

function getInitOptions(
    app: SentryVueInitOptions['app'],
    router: ReturnType<typeof useRouter>
): SentryVueInitOptions {
    return {
        app,
        dsn: environment.VITE_SENTRY_DSN_JS,
        environment: environment.VITE_SENTRY_ENVIRONMENT,
        integrations: [
            SentryVue.browserTracingIntegration({router}),
            SentryVue.extraErrorDataIntegration({depth: 3}),
            SentryVue.vueIntegration({
                tracingOptions: {
                    trackComponents: true,
                    hooks: ['create', 'mount', 'activate', 'destroy'],
                },
            }),
            SentryVue.replayIntegration(),
        ],
        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 0.5,
        tracesSampleRate: environment.VITE_SENTRY_TRACING_SAMPLE_RATE,
        ignoreErrors: ignoredErrorMessages,
        denyUrls: deniedUrls,
        beforeSend(event: ErrorEvent, hint: EventHint) {
            if (shouldDropEvent(event, hint)) {
                return null;
            }

            applyCustomFingerprint(event, hint);

            return isThrottled(event) ? null : event;
        },
    };
}

function shouldDropEvent(event: ErrorEvent, hint: EventHint): boolean {
    const error = hint?.originalException;

    if (!error) {
        return false;
    }

    // DOM Event rejections (e.g. a <script> failing to load) carry no
    // stacktrace and nothing actionable - drop them instead of letting
    // each one spawn its own issue.
    if (error instanceof Event) {
        return true;
    }

    if ([400, 401, 403, 404, 500].includes((error as {status?: number}).status!)) {
        return true;
    }

    const stackTrace = getStackTrace(event);

    return Boolean(stackTrace && stackTraceSentryExceptions.some(regex => regex.test(stackTrace)));
}

function applyCustomFingerprint(event: ErrorEvent, hint: EventHint) {
    const error = hint?.originalException;

    if (!error || typeof error === 'string') {
        return;
    }

    if (typeof (error as Error).message !== 'string') {
        // Stable fingerprint so non-Error rejections collapse into one
        // issue rather than one-per-event.
        event.fingerprint = ['non-error-rejection'];
    }
}

function getStackTrace(event: ErrorEvent) {
    try {
        return event.exception?.values
            ?.map(exception => exception.stacktrace?.frames?.map(frame => frame.function)?.join(''))
            ?.filter(Boolean)
            ?.join('');
    } catch {
        return null;
    }
}

function addErrorHandling(app: App) {
    // Hide console logs in prod
    if (!import.meta.env.DEV) {
        app.config.errorHandler = _ => {};
        return;
    }

    // Use default error handler -> throw errors
    if (environment.VITE_DEV_ERROR_HANDLING === 'throw') {
        return;
    }

    app.config.errorHandler = error => {
        // eslint-disable-next-line no-console
        console.error(error);
        if (environment.VITE_DEV_ERROR_HANDLING === 'alert') {
            useToast().error('A development error occurred.', {detail: error});
        }
    };
}
